import { PGA_COACH_PERSONAS, PGA_COACH_PERSONA_MAP } from '@lib/pga-coach/constants'
import isPgaLink from '@utils/isPgaLink'
import { coachIsPgaScheduleBookable } from '@lib/pga-coach/coach-scheduling'

function coachProfileBookingUrl(coach) {
  const raw = coach?.bookingUrl ?? coach?.bookingURL
  return typeof raw === 'string' ? raw.trim() : ''
}

// `isDesktop` is kept in the signature for caller compatibility — the
// intro video no longer drives meet-section content (it now lives in a
// dialog launched from the avatar play badge in the hero, regardless of
// viewport), so the parameter is unused.
// eslint-disable-next-line no-unused-vars
export function hasMeetSectionForCoach(coach, isDesktop) {
  const hasSkillTags = Array.isArray(coach.skills)
    && coach.skills.some((s) => s && typeof s.skillName === 'string' && s.skillName.trim())
  const hasToolTags = Array.isArray(coach.toolJsonData)
    && coach.toolJsonData.some((t) => typeof t === 'string' && t.trim())

  return Boolean(
    coach.bio
    || coach.numberOfStudents
    || coach.numberOfFollowers
    || coach.socialJsonData
    || hasSkillTags
    || hasToolTags,
  )
}

export function coachCanCollectLeads(coach) {
  const preferences = coach.coach?.preferences || {}
  const freeTierLimitsReached = coach.coach?.freeTierLimitsReached || []
  return Boolean(preferences.acceptsLeads && !freeTierLimitsReached.includes('LEADS'))
}

/**
 * Coach Tools "Book Now Preferences → External Tool" is not an enum on
 * the profile document. It is `bookNowEnabled` plus a booking URL that
 * is not a `*.pga.com` host (PGA Coach / my.pga.com schedulers). Missing
 * or invalid URLs return null so the Services shop can fall back to
 * PGA Coach listings (or hide).
 */
export function coachExternalBookingUrl(coach) {
  const { bookNowEnabled } = coach?.coach?.preferences || {}
  if (!bookNowEnabled) return null
  const url = coachProfileBookingUrl(coach)
  if (!url) return null
  let parsed
  try {
    parsed = new URL(url)
  } catch {
    return null
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
  if (!parsed.hostname) return null
  try {
    if (isPgaLink(url)) return null
  } catch {
    return null
  }
  return url
}

/**
 * Legacy sticky bottom CTA bar on small viewports — retired with the
 * header section nav + booking sidebar. Kept exported so callers can
 * gate padding consistently; always false on flagged profiles.
 */
export function coachProfileShowsStickyCtaBar() {
  return false
}

export function coachUsesExternalBookingTool(coach) {
  return Boolean(coachExternalBookingUrl(coach))
}

export function coachHasSchedulableBookings(coach) {
  const { bookNowEnabled } = coach.coach?.preferences || {}
  if (!bookNowEnabled) return false
  const url = coachProfileBookingUrl(coach)
  if (!url) return false
  const isExternalLink = !isPgaLink(url)
  const isBookable = coachIsPgaScheduleBookable(coach)
  return Boolean(isExternalLink || isBookable)
}

export function showCoachMeetContactTabs(coach, isDesktop) {
  if (coach.status !== 'ACTIVE') return false
  if (!hasMeetSectionForCoach(coach, isDesktop)) return false
  return coachCanCollectLeads(coach) || coachHasSchedulableBookings(coach)
}

/** DOM id for scrolling to the offerings shop (View Services). */
export const COACH_PROFILE_OFFERINGS_ANCHOR_ID = 'coach-profile-offerings-anchor'

/** DOM id for the in-page lead form (Send a Request / flagged `/contact` 302). */
export const COACH_PROFILE_CONTACT_ANCHOR_ID = 'coach-profile-contact'

export const PERSONA_PROFILE_TAB_LABELS = {
  'start-golf': 'Start Golf',
  'play-better': 'Play Better',
  'break-100': 'Break 100+',
  juniors: 'Juniors',
  'womens-golf': 'Women\'s Golf',
  'military-veterans': 'Military & Veterans',
}

export function isCoachProfilePersonaTabId(tabId) {
  return Boolean(tabId && PGA_COACH_PERSONA_MAP[tabId])
}

/**
 * URL <-> tab-id mapping for the coach profile section sub-pages.
 *
 * The canonical About surface lives at the bare /coach/<slug> URL (no path
 * segment). Internally that section is identified by the legacy tab id `meet`,
 * so the tab id is _not_ the same as the URL slug for About.
 *
 * Persona ids in `PGA_COACH_PERSONAS` are already kebab-case slugs, so the
 * persona tab id and URL section value are identical (`play-better`,
 * `juniors`, …). Contact also matches 1:1.
 *
 * Keep this mapping the single source of truth for both directions; tests and
 * the [section].js route both depend on it.
 */
const TAB_ID_TO_SECTION = {
  meet: 'about',
  contact: 'contact',
  'start-golf': 'start-golf',
  'play-better': 'play-better',
  'break-100': 'break-100',
  juniors: 'juniors',
  'womens-golf': 'womens-golf',
  'military-veterans': 'military-veterans',
}

const SECTION_TO_TAB_ID = Object.entries(TAB_ID_TO_SECTION).reduce(
  (acc, [tabId, section]) => {
    acc[section] = tabId
    return acc
  },
  {},
)

/**
 * Recognized URL section values for `/coach/[slug]/[section]`. `about` is
 * intentionally listed: a request to `/coach/<slug>/about` 301s to the
 * canonical `/coach/<slug>` instead of rendering duplicate content. Unknown
 * values 302 to `/coach/<slug>`.
 */
export const COACH_PROFILE_SECTION_VALUES = Object.values(TAB_ID_TO_SECTION)

/**
 * Convert a URL section value (`about`, `play-better`, …) to the internal
 * `profileTab` id used by `CoachBio` / `CategoryTabs`. Falls back to the
 * About tab id (`meet`) when the section is missing or unrecognized.
 */
export function tabIdFromSection(section) {
  if (!section) return 'meet'
  return SECTION_TO_TAB_ID[section] || 'meet'
}

/**
 * Convert a `profileTab` id back to its URL section value. Returns `null` for
 * non-section items (skeletons, dividers).
 */
export function sectionFromTabId(tabId) {
  if (!tabId) return null
  return TAB_ID_TO_SECTION[tabId] || null
}

/**
 * Build the canonical href for a coach profile section.
 *   - `meet` (About) → `/coach/<slug>` (no path segment, canonical)
 *   - `contact` / persona ids → `/coach/<slug>/<section>`
 *   - unknown ids → `/coach/<slug>` so the tab still navigates somewhere useful.
 */
export function buildCoachProfileSectionHref(slug, tabId) {
  if (!slug) return null
  const section = sectionFromTabId(tabId)
  if (!section || section === 'about') return `/coach/${slug}`
  return `/coach/${slug}/${section}`
}

/**
 * Keep the current query string (feature-flag overrides like
 * `?pga-com-coach-profile-ux=true`) when moving between About and
 * Contact. Hash-only hrefs are unchanged.
 */
export function preserveQueryOnHref(href, asPath) {
  if (!href || href.startsWith('#')) return href
  const search = (asPath || '').split('#')[0].split('?')[1]
  if (!search || href.includes('?')) return href
  const [path, hash] = href.split('#')
  return `${path}?${search}${hash ? `#${hash}` : ''}`
}

/**
 * Canonical About URL for `/coach/[slug]/[section]` redirects.
 * Preserves the incoming query (flag overrides like
 * `?pga-com-coach-profile-ux=true`) and optionally appends a hash.
 */
export function buildCoachProfileAboutRedirectLocation(slug, asPath, hash) {
  if (!slug) return '/'
  const search = String(asPath || '').split('#')[0].split('?')[1]
  const query = search ? `?${search}` : ''
  const fragment = hash ? `#${hash}` : ''
  return `/coach/${slug}${query}${fragment}`
}

export function scrollCoachProfileOfferingsIntoView() {
  if (typeof document === 'undefined') return
  const el = document.getElementById(COACH_PROFILE_OFFERINGS_ANCHOR_ID)
  if (!el) return
  const offset = 120
  window.scrollTo({ top: el.offsetTop - offset, behavior: 'smooth' })
}

/** In-page anchor ids for coach profile sections (hash deep-links). */
export const COACH_PROFILE_NAV_ANCHORS = {
  about: 'coach-profile-about',
  photos: 'coach-profile-photos',
  accreditations: 'coach-accreditations-row-heading',
  successStories: 'coach-profile-testimonials-heading',
  facilities: 'coach-facilities-heading',
  contact: COACH_PROFILE_CONTACT_ANCHOR_ID,
}

export function scrollCoachProfileSectionIntoView(anchorId) {
  if (typeof document === 'undefined' || !anchorId) return
  const el = document.getElementById(anchorId)
  if (!el) return
  const offset = 120
  const top = el.getBoundingClientRect().top + window.scrollY - offset
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
}

const COACH_PROFILE_SECTION_ANCHOR_IDS = new Set([
  COACH_PROFILE_OFFERINGS_ANCHOR_ID,
  ...Object.values(COACH_PROFILE_NAV_ANCHORS),
])

export function isCoachProfileSectionAnchorId(anchorId) {
  return COACH_PROFILE_SECTION_ANCHOR_IDS.has(anchorId)
}

/** Survives the About remount when Contact `router.push` drops the in-memory pending ref. */
export const COACH_PROFILE_PENDING_SCROLL_KEY = 'coach-profile-pending-section-scroll'
const PENDING_SCROLL_TTL_MS = 8000
const MAX_PENDING_SCROLL_FRAMES = 30

export function stashCoachProfilePendingScroll(anchorId) {
  if (typeof sessionStorage === 'undefined' || !anchorId) return
  try {
    sessionStorage.setItem(COACH_PROFILE_PENDING_SCROLL_KEY, JSON.stringify({
      anchorId,
      t: Date.now(),
    }))
  } catch {
    // private mode / quota
  }
}

export function takeCoachProfilePendingScroll() {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(COACH_PROFILE_PENDING_SCROLL_KEY)
    if (!raw) return null
    sessionStorage.removeItem(COACH_PROFILE_PENDING_SCROLL_KEY)
    const parsed = JSON.parse(raw)
    if (!parsed?.anchorId || !isCoachProfileSectionAnchorId(parsed.anchorId)) return null
    if (typeof parsed.t === 'number' && Date.now() - parsed.t > PENDING_SCROLL_TTL_MS) return null
    return parsed.anchorId
  } catch {
    return null
  }
}

/**
 * Photos live in `NoSsr` below CoachBio, so one rAF after About mounts
 * is often too soon. Retry until the node exists (or we hit the frame cap).
 */
export function scrollCoachProfileSectionIntoViewWhenPresent(anchorId, frames = 0) {
  if (typeof document === 'undefined' || !anchorId) return
  if (document.getElementById(anchorId)) {
    scrollCoachProfileSectionIntoView(anchorId)
    return
  }
  if (frames >= MAX_PENDING_SCROLL_FRAMES) return
  window.requestAnimationFrame(() => {
    scrollCoachProfileSectionIntoViewWhenPresent(anchorId, frames + 1)
  })
}

function coachHasAboutSection(coach) {
  return typeof coach?.bio === 'string' && coach.bio.trim().length > 0
}

function coachHasPhotosSection(coach) {
  return (coach?.photosJsonData?.length ?? 0) > 0
}

function coachHasTestimonialsSection(coach) {
  return Array.isArray(coach?.testimonialsJsonData)
    && coach.testimonialsJsonData.some((entry) => entry && entry.testimonial)
}

function coachProfileNavHashHref(slug, anchorId) {
  const aboutPath = slug ? buildCoachProfileSectionHref(slug, 'meet') : ''
  return aboutPath ? `${aboutPath}#${anchorId}` : `#${anchorId}`
}

/**
 * Hash destinations for the flagged coach profile (View Services,
 * Send a Request). The profile does **not** render a full-width tab
 * strip. Small-viewport booking-card jumps use
 * `buildCoachProfileBookingCardNavItems`. The Coaching Services
 * overview (`CoachServicesPanel`) is not mounted — Explore All
 * Services lived only on that panel.
 *
 * Destinations (omit empty sections):
 *  - About → `#coach-profile-about`
 *  - Services → offerings shop when present (`#coach-profile-offerings-anchor`)
 *  - Photos → `#coach-profile-photos`
 *  - Testimonials → `#coach-profile-testimonials-heading`
 *  - Credentials → `#coach-accreditations-row-heading`
 *  - Contact → `#coach-profile-contact`
 *
 * `sendRequest` stays on the model. Send a Request is an in-page hash
 * to `#coach-profile-contact` on the booking card / sticky bar. Flagged
 * `/coach/<slug>/contact` **302**s to that hash. Persona "Coaching For"
 * links are never shown. Unique offerings live on About
 * (`flattenUniqueCoachProfileOfferings`); leftover persona URL slugs
 * 302 to `/coach/<slug>`. `coachingFor` / `otherServices` stay empty
 * arrays so older callers keep a stable shape.
 */
export function buildCoachProfileSectionNav(coach = {}, options = {}) {
  const firstName = (coach?.firstName || '').trim() || 'Coach'
  const {
    hasAbout = coachHasAboutSection(coach),
    hasOfferings = false,
    hasPhotos = coachHasPhotosSection(coach),
    hasTestimonials = coachHasTestimonialsSection(coach),
    hasCredentials = false,
  } = options

  const slug = coach?.customSlug
  const sections = []

  if (hasAbout) {
    sections.push({
      id: 'about',
      label: 'About',
      href: coachProfileNavHashHref(slug, COACH_PROFILE_NAV_ANCHORS.about),
      anchorId: COACH_PROFILE_NAV_ANCHORS.about,
    })
  }

  if (hasOfferings) {
    sections.push({
      id: 'services',
      label: 'Services',
      href: coachProfileNavHashHref(slug, COACH_PROFILE_OFFERINGS_ANCHOR_ID),
      anchorId: COACH_PROFILE_OFFERINGS_ANCHOR_ID,
    })
  }

  if (hasPhotos) {
    sections.push({
      id: 'photos',
      label: 'Photos',
      href: coachProfileNavHashHref(slug, COACH_PROFILE_NAV_ANCHORS.photos),
      anchorId: COACH_PROFILE_NAV_ANCHORS.photos,
    })
  }

  if (hasTestimonials) {
    sections.push({
      id: 'testimonials',
      label: 'Testimonials',
      href: coachProfileNavHashHref(slug, COACH_PROFILE_NAV_ANCHORS.successStories),
      anchorId: COACH_PROFILE_NAV_ANCHORS.successStories,
    })
  }

  if (hasCredentials) {
    sections.push({
      id: 'credentials',
      label: 'Credentials',
      href: coachProfileNavHashHref(slug, COACH_PROFILE_NAV_ANCHORS.accreditations),
      anchorId: COACH_PROFILE_NAV_ANCHORS.accreditations,
    })
  }

  return {
    about: {
      id: 'meet',
      label: `About ${firstName}`,
    },
    coachingFor: [],
    otherServices: [],
    sections,
    sendRequest: coachCanCollectLeads(coach)
      ? { id: 'contact', label: 'Send a Request' }
      : null,
    loading: false,
  }
}

/**
 * Small-viewport booking-card section jumps. Order matches the
 * product list: Overview, Services, Photos, Testimonials, Location(s),
 * Ask the Coach. Omit a link when that section is absent. The flagged
 * profile does not render FAQs. Header nav uses pill buttons.
 */
export function buildCoachProfileBookingCardNavItems(coach = {}, options = {}) {
  const {
    hasAbout = coachHasAboutSection(coach),
    hasOfferings = false,
    hasContact = coachCanCollectLeads(coach),
    hasPhotos = coachHasPhotosSection(coach),
    hasTestimonials = coachHasTestimonialsSection(coach),
    hasFacilities = false,
  } = options

  const slug = coach?.customSlug
  const items = []

  if (hasAbout) {
    items.push({
      id: 'about',
      label: 'Overview',
      href: coachProfileNavHashHref(slug, COACH_PROFILE_NAV_ANCHORS.about),
      anchorId: COACH_PROFILE_NAV_ANCHORS.about,
    })
  }

  if (hasOfferings) {
    items.push({
      id: 'services',
      label: 'Services',
      href: coachProfileNavHashHref(slug, COACH_PROFILE_OFFERINGS_ANCHOR_ID),
      anchorId: COACH_PROFILE_OFFERINGS_ANCHOR_ID,
    })
  }

  if (hasPhotos) {
    items.push({
      id: 'photos',
      label: 'Photos',
      href: coachProfileNavHashHref(slug, COACH_PROFILE_NAV_ANCHORS.photos),
      anchorId: COACH_PROFILE_NAV_ANCHORS.photos,
    })
  }

  if (hasTestimonials) {
    items.push({
      id: 'testimonials',
      label: 'Testimonials',
      href: coachProfileNavHashHref(slug, COACH_PROFILE_NAV_ANCHORS.successStories),
      anchorId: COACH_PROFILE_NAV_ANCHORS.successStories,
    })
  }

  if (hasFacilities) {
    const facilityCount = coach?.facilities?.length ?? 0
    items.push({
      id: 'facilities',
      label: facilityCount > 1 ? 'Locations' : 'Location',
      href: coachProfileNavHashHref(slug, COACH_PROFILE_NAV_ANCHORS.facilities),
      anchorId: COACH_PROFILE_NAV_ANCHORS.facilities,
    })
  }

  if (hasContact) {
    items.push({
      id: 'contact',
      label: 'Ask the Coach',
      href: coachProfileNavHashHref(slug, COACH_PROFILE_NAV_ANCHORS.contact),
      anchorId: COACH_PROFILE_NAV_ANCHORS.contact,
    })
  }

  return items
}

/**
 * Count of in-page hash destinations. Send a Request is a booking-card
 * action, not a destination. The flagged profile does not render a
 * full-width section strip from this count.
 */
export function countCoachProfileSectionNavDestinations(nav) {
  if (!nav) return 0
  return nav.sections?.length || 0
}

// populatedPersonaIds: null = loading (show skeletons), Set/array = filter to populated IDs, undefined = show all
export function buildCoachProfileSectionTabItems(coach = {}, populatedPersonaIds = undefined) {
  const includeContactTab = coachCanCollectLeads(coach)
  const base = [
    { id: 'meet', label: 'About' },
  ]
  if (includeContactTab) {
    base.push(
      {
        id: 'contact',
        label: 'Contact the Coach',
        showIconOnly: true,
        icon: 'chat-bubble-outline',
      },
    )
  }

  // null means still loading — show divider + skeleton placeholders to prevent layout shift
  if (populatedPersonaIds === null) {
    base.push(
      { id: 'coach-profile-section-divider', variant: 'divider' },
      { id: 'skeleton-tab-1', variant: 'skeleton', width: 84 },
      { id: 'skeleton-tab-2', variant: 'skeleton', width: 96 },
      { id: 'skeleton-tab-3', variant: 'skeleton', width: 72 },
      { id: 'skeleton-tab-4', variant: 'skeleton', width: 88 },
    )
    return base
  }

  let populatedSet = null
  if (populatedPersonaIds instanceof Set) {
    populatedSet = populatedPersonaIds
  } else if (Array.isArray(populatedPersonaIds)) {
    populatedSet = new Set(populatedPersonaIds)
  }

  const personaItems = PGA_COACH_PERSONAS
    .filter((persona) => !populatedSet || populatedSet.has(persona.id))
    .map((persona) => ({
      id: persona.id,
      label: PERSONA_PROFILE_TAB_LABELS[persona.id] || persona.label,
    }))

  if (personaItems.length === 0) {
    return base
  }

  base.push(
    { id: 'coach-profile-section-divider', variant: 'divider' },
    ...personaItems,
  )
  return base
}
